import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, ClickEvent, Component, ComponentFactory, CrudDecorator, Cursor, Image, Label, LabelProps, Panel, PermDefinitionConstants, TreeNode, TreeNodeProps } from "@mcleod/components";
import { Alignment, VerticalAlignment } from "@mcleod/core";
import { RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";
import { PermissionsDesigner } from "./PermissionsDesigner";
import { PermissionsSource } from "./PermissionsSource";
import { SecureItem } from "./SecureItem";
import { PermissionsMain } from "./PermissionsMain";
import { CommonDialogs } from "../CommonDialogs";

export interface PermsMenuTreeNodeProps extends TreeNodeProps {
    title: string;
    nestedLayoutPaths: string[];
    permissionsMain: PermissionsMain;
    secureItem: SecureItem;
}

export class PermsMenuTreeNode extends TreeNode {
    labelTitle: Label;
    secureImage: Image;
    groupImage: Image;
    nestedLayoutPaths: string[];
    permissionsMain: PermissionsMain;
    permissionsSource: PermissionsSource;
    title: string;
    secureItem: SecureItem;

    constructor(path: string, isAction: boolean, actionPath: string = RowPermsSecureItem.ACTION, props: Partial<PermsMenuTreeNodeProps>) {
        super({
            component: new Panel({ fillRow: false, verticalAlign: VerticalAlignment.CENTER, margin:0, padding:0 }),
            paddingTop:0,
            paddingBottom:0,
            ...props
        });

        this.permissionsSource = this.permissionsMain?.permissionsSource;

        if (this.secureItem == null) {
            this.secureItem = new SecureItem({
                isAction: isAction,
                pageIdentifier: isAction ? actionPath : path,
                itemName: isAction ? path : RowPermsSecureItem.ENTIRE_SCREEN,
                itemDescription: !isAction ? RowPermsSecureItem.ENTIRE_SCREEN : (actionPath === RowPermsSecureItem.DRIVER_APPLICATION ? this.title : path),
                permsDef: isAction ? PermDefinitionConstants.ACTION : PermDefinitionConstants.ENTIRE_SCREEN_VIEW,
                permsChanged: () => this.syncUI(),
                allowDenyChanged: () => this.syncUI()
            })
        }
        this.buildUI(this.component as Panel);
    }

    private buildUI(panel: Panel) {
        this.secureImage = new Image({ rowBreak: false, color: "primary", paddingRight: 8 });
        this.groupImage = new Image({ rowBreak: false, color: "primary", paddingRight: 4, height: 20, width: 26 });
        const labelPanel = Panel.createNoMarginNoPaddingPanel({ rowBreak: false, minWidth: 280});
        this.labelTitle = new Label({ id: "labelMenuCaption", text: this.title, cursor: Cursor.POINTER, allowSelect: false });
        labelPanel.add(this.labelTitle);
        panel.add(this.secureImage, this.groupImage, labelPanel);
        this.syncUI();
        if (this.secureItem.isAction != true) {
            panel.add(this.createFieldLevelPermsButton(this.secureItem.pageIdentifier));
        }
    }

    createFieldLevelPermsButton(path: string) {
        const button = new Button({
            id: "buttonDetail",
            imageName: ImageName.arrowGoTo,
            tooltip: "Open for editing field-level permissions",
            variant: ButtonVariant.round,
            rowBreak: false
        });
        button.addClickListener((event: ClickEvent) => {
            this.getTree().selectedNode = this;
            this.showFieldLevelDesigner(path, event);
        });
        return button;
    }

    syncUI() {
        if (this.groupImage == null)
            return;
        this.groupImage.name = ImageName.userThree;
        if (this.rowPermsSecureItem == null) {
            this.secureImage.name = ImageName.visible;
        } else {
            if (this.getAllowDenyRows()?.length > 0)
                this.groupImage.name = ImageName.userThreeBlueMiddle;
            this.secureImage.name = ImageName.visibleOff;
        }

        if (this.secureItem.isAction != true) {
            this.labelTitle.color = this.hasFieldLevelPerms ? "error.light" : null;
            this.labelTitle.tooltipDynamicContent = () => this.createTitleTooltipPanel();
            this.labelTitle.tooltipDynamicOptions = { position: Alignment.RIGHT };
        }
    }

    private createTitleTooltipPanel(): Panel {
        const nestedLayoutsWithFieldLevelPerms = this.nestedLayoutsWithFieldLevelPerms;
        const hasFieldLevelPerms = nestedLayoutsWithFieldLevelPerms?.length > 0 || this.hasDirectFieldLevelPerms;
        if (hasFieldLevelPerms != true)
            return null;
        const panel = Panel.createNoMarginNoPaddingPanel();
        if (nestedLayoutsWithFieldLevelPerms?.length > 0) {
            let suffix = "nested layouts";
            if (this.hasDirectFieldLevelPerms)
                suffix = "this layout and " + suffix;
            panel.add(new Label({ text: `Field-level permissions are set on ${suffix}`, fontBold: true }));

            const labelProps: Partial<LabelProps>[] = [];
            for (const layout of nestedLayoutsWithFieldLevelPerms) {
                labelProps.push({
                    text: layout,
                    tooltip: "Open for editing field-level permissions",
                    color: "primary",
                    onClick: (event:ClickEvent) => this.showFieldLevelDesigner(layout, event)
                });
            }

            panel.add(...ComponentFactory.createBulletedList(labelProps));
        }
        else {
            panel.add(new Label({ text: "Field-level permissions are set on this layout"}));
        }
        return panel;
    }

    get rowPermsSecureItem(): RowPermsSecureItem {
        return this.permissionsSource?.getSecureItemRow(this.secureItem);
    }

    get hasFieldLevelPerms(): boolean {
        return this.hasDirectFieldLevelPerms || this.nestedLayoutsWithFieldLevelPerms?.length > 0;
    }

    get hasDirectFieldLevelPerms() {
        return this.permissionsSource?.hasFieldLevelSecureItemRows(this.secureItem.pageIdentifier);
    }

    get nestedLayoutsWithFieldLevelPerms(): string[] {
        return this.nestedLayoutPaths?.filter(path => this.permissionsSource?.getSecureItemRowsForPage(path)?.length > 0) ?? [];
    }

    getAllowDenyRows(): RowPermsAllowDeny[] {
        return this.permissionsSource?.getAllowDenyRows(this.secureItem);
    }

    showFieldLevelDesigner(path: string, event: ClickEvent) {
        const comp = event.target as Component;
        let labelOrButton: Label | Button;
        if (comp instanceof Button || comp instanceof Label) {
            labelOrButton = comp;
            labelOrButton.busy = true;
        }

        PermissionsDesigner.slideUp(path, this.permissionsSource).then((crudDecorator: CrudDecorator) => {
            if (crudDecorator != null) {
                crudDecorator.doAfterSlideOut = () => this.permissionsMain.syncMenuTreeNodes();
            } else {
                CommonDialogs.showDialog("Field-level permissions cannot be set on this item");
            }
        }).finally(() => {
            if (labelOrButton != null)
                labelOrButton.busy = false;
        });
    }
}
